// What a student reads about a booking payment (ADR 0011): the outcome in glossary words, the
// amount in pesos, the next step, and the hand-off from JTPay's return address to the result page.
// Pure, so every rule here is unit-tested without a browser; fetchTestPayments is the one API read.
import { api } from './api';

// Shown next to Pay, on the result page and on admin booking payments while JTPay's app key is a
// test key (JTP-27).
export const TEST_PAYMENTS_NOTE = 'Test payments: JTPay staging. No real money moves.';

// One read per page load, shared by every line on the page. False on any error, so a failed read
// never shows the line.
let testPaymentsRead = null;

export function fetchTestPayments() {
  if (!testPaymentsRead) {
    testPaymentsRead = Promise.resolve()
      .then(() => api.get('/booking-payments/mode'))
      .then((data) => data?.testPayments === true)
      .catch(() => false);
  }
  return testPaymentsRead;
}

// The words for a student Pay applies to (JTP-29): the caption under an instructor's booking card
// and the How do I pay? answer on the Contact page. Everyone else reads the page's own words.
export const PAY_THROUGH_ROPIDA_CARD = 'Pay through Ropida with Pay (JTPay) · Confirmed after payment';
export const PAY_THROUGH_ROPIDA_ANSWER = "Through Ropida, with Pay (JTPay). After you book a seat, open My Bookings and press Pay. You pay on JTPay's pay page, and your booking is confirmed once JTPay reports your payment Paid.";

// The rest of the pages that told a student Pay applies to to pay the instructor directly (JTP-31):
// the Pricing page payment row, step 3 of How Ropida works and the first-session answer.
export const PAY_THROUGH_ROPIDA_PRICING_ROW = {
  label: 'Pay through Ropida',
  price: 'Pay (JTPay)',
  note: 'Press Pay in My Bookings; your booking is confirmed once JTPay reports it Paid',
};
export const PAY_THROUGH_ROPIDA_STEP = {
  title: 'Pay through Ropida',
  desc: "After you book a seat, open My Bookings and press Pay. You pay on JTPay's pay page, and your booking is confirmed once JTPay reports your payment Paid — you'll get an email when it's confirmed.",
};
export const PAY_THROUGH_ROPIDA_FIRST_SESSION = "Browse instructors, open a profile, and pick one of their upcoming sessions. After you book a seat, open My Bookings and press Pay. You pay through Ropida on JTPay's pay page, and your booking is confirmed once JTPay reports your payment Paid.";

// Whether Pay applies to this user, read from GET /bookings/my (the same answer BookingModal uses).
// Only a signed-in student has an answer, so anyone else resolves false with no request. Cached per
// user id, so a sign-in or sign-out in the same page load reads again. False on any error.
let payAppliesRead = { id: null, promise: null };

export function fetchPayApplies(user) {
  if (!user || user.role !== 'student') return Promise.resolve(false);
  if (payAppliesRead.id !== user.id || !payAppliesRead.promise) {
    payAppliesRead = {
      id: user.id,
      promise: Promise.resolve()
        .then(() => api.get('/bookings/my'))
        .then((data) => data?.payAvailable === true)
        .catch(() => false),
    };
  }
  return payAppliesRead.promise;
}

// The page a student lands on when they come back from JTPay's pay page.
export const PAY_RESULT_PAGE = 'pay-result';

// JTPay sends the student back to `<site>/pay/result?reference=<ref>` (the API's return address).
const PAY_RESULT_PATH = /^\/pay\/result\/?$/;
// The SPA lives under /app/ (Caddyfile); its pages hang off the hash.
const APP_PATH = '/app/';

// Where a booking payment stands, as JTPay reports it. The API sends the lower-case keys.
export const OUTCOME = {
  WAITING: 'waiting',
  CONFIRMING: 'confirming',
  PAID: 'paid',
  UNDERPAID: 'underpaid',
  OVERPAID: 'overpaid',
  PAID_MULTIPLE: 'paid_multiple',
  EXPIRED: 'expired',
  FAILED: 'failed',
};

// The glossary words for each outcome (docs/GLOSSARY.md, Outcome).
export const OUTCOME_LABEL = {
  [OUTCOME.WAITING]: 'Waiting',
  [OUTCOME.CONFIRMING]: 'Confirming',
  [OUTCOME.PAID]: 'Paid',
  [OUTCOME.UNDERPAID]: 'Underpaid',
  [OUTCOME.OVERPAID]: 'Overpaid',
  [OUTCOME.PAID_MULTIPLE]: 'Paid more than once',
  [OUTCOME.EXPIRED]: 'Expired',
  [OUTCOME.FAILED]: 'Failed',
};

// What the student does next, one per outcome. Each says what happened, then what to do.
export const OUTCOME_NEXT_STEP = {
  [OUTCOME.WAITING]: 'JTPay has not received your payment yet. If you paid, wait a minute and check again. If not, open your bookings and press Pay.',
  [OUTCOME.CONFIRMING]: 'JTPay is confirming your payment. Your bookings are confirmed once it is Paid. Check again in a minute.',
  [OUTCOME.PAID]: 'Your payment arrived. Your bookings are confirmed within a minute, and the meeting link shows on your bookings.',
  [OUTCOME.UNDERPAID]: 'JTPay received less than the amount, so your bookings stay pending. Open your bookings and press Pay to pay again. Ropida refunds the short payment by hand.',
  [OUTCOME.OVERPAID]: 'JTPay received more than the amount. Your bookings are confirmed, and Ropida refunds the difference by hand.',
  [OUTCOME.PAID_MULTIPLE]: 'You paid more than once. Your bookings are confirmed, and Ropida refunds the extra payment by hand.',
  [OUTCOME.EXPIRED]: 'This payment expired before your money arrived, so your bookings stay pending. Open your bookings and press Pay to start a new payment.',
  [OUTCOME.FAILED]: 'This payment failed, so your bookings stay pending. Open your bookings and press Pay to try again.',
};

// Outcomes that still hold their bookings: the student finishes paying on the same pay page.
const OPEN_OUTCOMES = new Set([OUTCOME.WAITING, OUTCOME.CONFIRMING]);

export function outcomeLabel(outcome) {
  return OUTCOME_LABEL[outcome] || 'Unknown';
}

export function isOpenOutcome(outcome) {
  return OPEN_OUTCOMES.has(outcome);
}

// Pesos with two decimals and thousands grouped: '2400.00' or 2400 -> '₱2,400.00'. Built by hand,
// not with toLocaleString, so the amount reads the same in every browser and locale. Null when the
// value is not an amount, so a caller never shows '₱NaN'.
export function formatPesos(value) {
  const text = typeof value === 'number' ? value.toFixed(2) : String(value ?? '').trim();
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return null;
  const whole = match[1].replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cents = (match[2] || '').padEnd(2, '0');
  return `₱${whole}.${cents}`;
}

// A pending booking a booking payment holds: the student finishes paying on its pay page.
export function isWaitingForPayment(booking) {
  return Boolean(
    booking
      && booking.status === 'pending'
      && booking.bookingPayment
      && isOpenOutcome(booking.bookingPayment.outcome),
  );
}

// A pending booking the student pays for through Ropida: Pay shows instead of the instructor's
// payment instructions and the screenshot step.
export function showsPay(booking) {
  return Boolean(booking && booking.status === 'pending' && booking.payWithRopida);
}

// The result page's address for JTPay's return address, or null for any other address. The
// reference is passed on as given; the API checks it and answers 404 for anything not the
// student's own.
//
// `location` is { pathname, search }, like window.location.
export function payResultHandoff(location) {
  if (!location || !PAY_RESULT_PATH.test(location.pathname || '')) return null;
  const reference = new URLSearchParams(location.search || '').get('reference');
  const query = reference ? `?reference=${encodeURIComponent(reference)}` : '';
  return `${APP_PATH}#/${PAY_RESULT_PAGE}${query}`;
}

// The reference in the result page's hash (`#/pay-result?reference=<ref>`), or null.
export function referenceFromHash(hash) {
  const text = String(hash ?? '');
  const q = text.indexOf('?');
  if (q === -1) return null;
  return new URLSearchParams(text.slice(q + 1)).get('reference') || null;
}
